/**
 * KI-Analyse & Prognose — Umsatz-Vorhersage mit Konfidenzband, automatisch
 * generierte Insight-Karten und Anomalie-Tabelle. Reagiert wie alle Seiten
 * auf die globalen Filter (Filiale, Warengruppe, Artikel, Zeitraum …).
 *
 * Signatur der Seite: der Prognose-Chart mit klar abgetrennter Zukunfts-Zone
 * hinter der „Stand"-Linie — Vergangenheit solide, Zukunft gestrichelt im
 * violetten Band.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import type { InsightCard, InsightsResponse } from '@statistik/shared-types';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Panel, ReportHeader } from '../components/Panel.tsx';
import { Icon } from '../components/Icon.tsx';
import { ChartSkeleton } from '../components/Skeleton.tsx';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { useIsDark } from '../lib/useIsDark.ts';
import {
  fmtCurrency,
  fmtCurrencyCompact,
  fmtDate,
  fmtPercent,
  fmtWochentag,
} from '../lib/format.ts';

const FORECAST_COLOR = '#8b5cf6'; // violet-500 — Zukunft klar vom Ist (brand-blau) getrennt

export function KiAnalysePage() {
  const filters = useFilterStore((s) => s.filters);
  const [horizon, setHorizon] = useState<number>(28);
  const { data, isLoading } = useQuery({
    queryKey: ['insights', filters, horizon],
    queryFn: () => api.insights({ filters, horizon }),
  });

  return (
    <div className="space-y-6 w-full max-w-[1400px] mx-auto">
      <ReportHeader
        title="KI-Analyse & Prognose"
        desc="Umsatz-Vorhersage, automatisch erkannte Auffälligkeiten und generierte Erkenntnisse — berechnet aus Wochen-Saisonalität und Trend deiner Daten. Filter links gelten auch hier."
      />

      {isLoading || !data ? (
        <ChartSkeleton height={420} />
      ) : (
        <>
          <SummaryTiles data={data} />

          <Panel
            title="Umsatz-Prognose"
            subtitle={
              data.summary.stand
                ? `Historie bis ${fmtDate(data.summary.stand)}, Prognose ${horizon} Tage mit ~80%-Konfidenzband`
                : 'Keine Daten im gewählten Bereich'
            }
          >
            <div className="mb-3 flex items-center justify-end">
              <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                {[14, 28, 56].map((h) => (
                  <button
                    key={h}
                    onClick={() => setHorizon(h)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                      horizon === h
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    {h} Tage
                  </button>
                ))}
              </div>
            </div>
            <ForecastChart data={data} />
            <p className="mt-2 text-2xs text-slate-400 dark:text-slate-500">
              Methode: Holt-Winters (additive Wochen-Saisonalität, Parameter automatisch
              kalibriert) — vollständig aus den eigenen Kassendaten berechnet, ohne externe Dienste.
            </p>
          </Panel>

          {data.insights.length > 0 && (
            <div className="space-y-3">
              <div className="section-head">
                <h3 className="section-eyebrow">Erkenntnisse</h3>
                <span className="badge text-2xs">{data.insights.length}</span>
                <div className="section-rule" />
                <span className="section-hint truncate">
                  Automatisch aus den gefilterten Daten abgeleitet.
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.insights.map((card) => (
                  <InsightCardItem key={card.id} card={card} />
                ))}
              </div>
            </div>
          )}

          {data.anomalies.length > 0 && (
            <Panel
              title="Auffällige Tage"
              subtitle="Tage, die deutlich vom üblichen Niveau ihres Wochentags abweichen (letzte 6 Wochen)"
            >
              <AnomalyTable data={data} />
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

/* ── Summary-Kacheln ─────────────────────────────────────────── */

function SummaryTiles({ data }: { data: InsightsResponse }) {
  const s = data.summary;
  const d7 = s.letzte_7t > 0 ? (s.prognose_7t - s.letzte_7t) / s.letzte_7t : null;
  const dMonat = s.monat_vormonat > 0 ? (s.monat_hochrechnung - s.monat_vormonat) / s.monat_vormonat : null;
  const tiles: Array<{ label: string; value: string; delta: number | null; hint: string }> = [
    {
      label: 'Prognose nächste 7 Tage',
      value: fmtCurrencyCompact(s.prognose_7t),
      delta: d7 !== null ? d7 * 100 : null,
      hint: `letzte 7 Tage: ${fmtCurrencyCompact(s.letzte_7t)}`,
    },
    {
      label: 'Monats-Hochrechnung',
      value: fmtCurrencyCompact(s.monat_hochrechnung),
      delta: dMonat !== null ? dMonat * 100 : null,
      hint: `Ist bisher: ${fmtCurrencyCompact(s.monat_ist)}`,
    },
    {
      label: 'Trend 28 Tage',
      value: s.trend_28t_pct !== null ? fmtPercent(s.trend_28t_pct / 100) : '—',
      delta: s.trend_28t_pct,
      hint: 'vs. die 28 Tage davor',
    },
    {
      label: 'Vorjahresvergleich',
      value: s.vorjahr_28t_pct !== null ? fmtPercent(s.vorjahr_28t_pct / 100) : '—',
      delta: s.vorjahr_28t_pct,
      hint: 'letzte 28 Tage vs. Vorjahr',
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((t) => (
        <div key={t.label} className="card">
          <div className="card-body py-4">
            <p className="text-2xs uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
              {t.label}
            </p>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-xl font-bold tabular-nums text-slate-900 dark:text-slate-100 font-mono">
                {t.value}
              </span>
              {t.delta !== null && Math.abs(t.delta) >= 0.05 && (
                <span
                  className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${
                    t.delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                  }`}
                >
                  <Icon name={t.delta >= 0 ? 'arrowUp' : 'arrowDown'} size={11} />
                  {fmtPercent(Math.abs(t.delta) / 100)}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-2xs text-slate-400 dark:text-slate-500">{t.hint}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Prognose-Chart ──────────────────────────────────────────── */

function ForecastChart({ data }: { data: InsightsResponse }) {
  const dark = useIsDark();
  const history = data.history.slice(-90);
  const fc = data.forecast;
  const labels = [...history.map((p) => p.datum), ...fc.map((p) => p.datum)];
  const nHist = history.length;

  // Serien über die Gesamt-Achse: Vergangenheit | Zukunft (mit 1 Überlapp-Punkt,
  // damit die Linien nahtlos ineinander übergehen).
  const istData = labels.map((_, i) => (i < nHist ? history[i].umsatz : null));
  const fcData = labels.map((_, i) =>
    i >= nHist ? fc[i - nHist].prognose : i === nHist - 1 ? history[i].umsatz : null,
  );
  const loData = labels.map((_, i) => (i >= nHist ? fc[i - nHist].lo : null));
  const bandData = labels.map((_, i) => (i >= nHist ? fc[i - nHist].hi - fc[i - nHist].lo : null));

  const anomalySet = new Map(data.anomalies.map((a) => [a.datum, a]));

  const option = {
    grid: { top: 28, right: 30, bottom: 40, left: 70, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      formatter: (
        params: Array<{ axisValue: string; seriesName: string; value: number | null; marker: string }>,
      ) => {
        const ts = params[0]?.axisValue ?? '';
        const rows = params
          .filter((p) => p.value != null && p.seriesName !== '_lo' && p.seriesName !== '_band')
          .map(
            (p) =>
              `<div style="display:flex;justify-content:space-between;gap:16px;font-size:12px;line-height:1.6">
                 <span>${p.marker} ${p.seriesName}</span>
                 <span style="font-weight:600;font-variant-numeric:tabular-nums">${fmtCurrency(Number(p.value))}</span>
               </div>`,
          )
          .join('');
        const a = anomalySet.get(ts);
        const anomalyRow = a
          ? `<div style="margin-top:4px;font-size:11px;color:${a.richtung === 'tief' ? '#f97066' : '#12b76a'}">⚠ ${a.richtung === 'tief' ? 'deutlich unter' : 'deutlich über'} Wochentags-Niveau (${fmtPercent(a.abweichung_pct)})</div>`
          : '';
        return `<div><div style="font-weight:600;margin-bottom:4px;font-size:12px">${fmtDate(ts)}</div>${rows}${anomalyRow}</div>`;
      },
    },
    legend: {
      data: [
        { name: 'Umsatz (Ist)', icon: 'circle' },
        { name: 'Prognose', icon: 'circle' },
      ],
      textStyle: { fontSize: 11, color: dark ? COLORS.darkText : COLORS.text },
      top: 0,
      right: 0,
      itemWidth: 8,
      itemHeight: 8,
    },
    xAxis: {
      type: 'category',
      data: labels,
      ...axisStyle(),
      boundaryGap: false,
      axisLabel: {
        ...axisStyle().axisLabel,
        formatter: (v: string) => {
          const [, m, d] = v.split('-');
          return `${d}.${m}`;
        },
      },
    },
    yAxis: {
      type: 'value',
      ...axisStyle(),
      axisLabel: { ...axisStyle().axisLabel, formatter: (v: number) => fmtCurrencyCompact(v) },
    },
    series: [
      // Konfidenzband: unsichtbare lo-Basis + gestapelte Bandbreite
      {
        name: '_lo',
        type: 'line',
        stack: 'band',
        data: loData,
        lineStyle: { opacity: 0 },
        symbol: 'none',
        silent: true,
        tooltip: { show: false },
      },
      {
        name: '_band',
        type: 'line',
        stack: 'band',
        data: bandData,
        lineStyle: { opacity: 0 },
        symbol: 'none',
        silent: true,
        tooltip: { show: false },
        areaStyle: { color: FORECAST_COLOR, opacity: dark ? 0.14 : 0.1 },
      },
      {
        name: 'Umsatz (Ist)',
        type: 'line',
        smooth: 0.15,
        showSymbol: false,
        data: istData,
        lineStyle: { width: 2, color: COLORS.primary },
        itemStyle: { color: COLORS.primary },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(31,151,245,0.14)' },
              { offset: 1, color: 'rgba(31,151,245,0)' },
            ],
          },
        },
        markLine: {
          silent: true,
          symbol: 'none',
          data: nHist > 0 ? [{ xAxis: history[nHist - 1].datum }] : [],
          lineStyle: { color: dark ? '#94a3b8' : '#64748b', type: [4, 4], width: 1 },
          label: {
            formatter: 'Stand',
            position: 'insideEndTop',
            fontSize: 10,
            color: dark ? '#94a3b8' : '#64748b',
          },
        },
        // Anomalie-Punkte direkt im Ist-Verlauf markieren
        markPoint: {
          silent: true,
          symbol: 'circle',
          symbolSize: 7,
          data: data.anomalies
            .filter((a) => history.some((h) => h.datum === a.datum))
            .map((a) => ({
              coord: [a.datum, a.ist],
              itemStyle: {
                color: a.richtung === 'tief' ? COLORS.negative : COLORS.positive,
                borderColor: dark ? '#0f172a' : '#ffffff',
                borderWidth: 2,
              },
            })),
          label: { show: false },
        },
      },
      {
        name: 'Prognose',
        type: 'line',
        smooth: 0.15,
        showSymbol: false,
        data: fcData,
        lineStyle: { width: 2, color: FORECAST_COLOR, type: [6, 3] },
        itemStyle: { color: FORECAST_COLOR },
      },
    ],
  };

  return (
    <ReactECharts option={option} style={{ height: 420 }} opts={{ renderer: 'canvas' }} notMerge={true} />
  );
}

/* ── Insight-Karten ──────────────────────────────────────────── */

const SEVERITY_STYLES: Record<InsightCard['severity'], { border: string; icon: string; iconName: 'trendingUp' | 'trendingDown' | 'alertTriangle' | 'sparkles' | 'clock' }> = {
  positiv: {
    border: 'border-l-emerald-500',
    icon: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400',
    iconName: 'trendingUp',
  },
  neutral: {
    border: 'border-l-slate-300 dark:border-l-slate-600',
    icon: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
    iconName: 'sparkles',
  },
  warnung: {
    border: 'border-l-amber-500',
    icon: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400',
    iconName: 'trendingDown',
  },
  kritisch: {
    border: 'border-l-red-500',
    icon: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400',
    iconName: 'alertTriangle',
  },
};

function InsightCardItem({ card }: { card: InsightCard }) {
  const s = SEVERITY_STYLES[card.severity] ?? SEVERITY_STYLES.neutral;
  return (
    <div className={`card border-l-2 ${s.border}`}>
      <div className="card-body py-4 flex gap-3">
        <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${s.icon}`}>
          <Icon name={s.iconName} size={16} />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
            {card.titel}
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {card.text}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Anomalie-Tabelle ────────────────────────────────────────── */

function AnomalyTable({ data }: { data: InsightsResponse }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-2xs uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <th className="py-2 pr-4 font-semibold">Datum</th>
            <th className="py-2 pr-4 font-semibold">Tag</th>
            <th className="py-2 pr-4 font-semibold text-right">Ist</th>
            <th className="py-2 pr-4 font-semibold text-right">Üblich (Median)</th>
            <th className="py-2 pr-4 font-semibold text-right">Abweichung</th>
            <th className="py-2 font-semibold">Einordnung</th>
          </tr>
        </thead>
        <tbody>
          {data.anomalies.map((a) => (
            <tr
              key={a.datum}
              className="border-b border-slate-100 dark:border-slate-800 last:border-0"
            >
              <td className="py-2 pr-4 tabular-nums font-mono text-xs">{fmtDate(a.datum)}</td>
              <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{fmtWochentag(a.wochentag)}</td>
              <td className="py-2 pr-4 text-right tabular-nums font-mono text-xs">{fmtCurrency(a.ist)}</td>
              <td className="py-2 pr-4 text-right tabular-nums font-mono text-xs text-slate-500 dark:text-slate-400">
                {fmtCurrency(a.erwartet)}
              </td>
              <td
                className={`py-2 pr-4 text-right tabular-nums font-mono text-xs font-semibold ${
                  a.richtung === 'tief' ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                }`}
              >
                {fmtPercent(a.abweichung_pct)}
              </td>
              <td className="py-2">
                <span
                  className={`badge text-2xs ${
                    a.richtung === 'tief'
                      ? 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400'
                      : 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {a.richtung === 'tief' ? 'Einbruch' : 'Ausreißer nach oben'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
